/**
 * DateUtils 对象，包含所有日期工具方法
 */
const DateUtils = {

  /**
   * 将毫秒数格式化为人类可读的时间字符串
   *
   * @param {number} ms - 毫秒数
   * @param {Object} options - 配置选项
   * @param {boolean} options.showSeconds - 是否显示秒，默认 false
   * @param {string} options.format - 输出格式：'auto'(自动) | 'full'(完整) | 'short'(简短)，默认 'auto'
   * @returns {string} 格式化后的时间字符串，如："1天2小时3分钟"
   * @example
   * DateUtils.formatDuration(3600000) // "1小时"
   * DateUtils.formatDuration(90061000) // "1天1小时1分钟"
   * DateUtils.formatDuration(3600000, { showSeconds: true }) // "1小时0分钟0秒"
   * DateUtils.formatDuration(3661000, { format: 'full' }) // "0天1小时1分钟"
   * DateUtils.formatDuration(3661000, { format: 'short' }) // "1小时"
   */
  formatDuration(ms, options = {}) {
    // 参数校验
    if (ms === null || ms === undefined || isNaN(ms)) {
      return '-';
    }

    // 处理负数
    const isNegative = ms < 0;
    const absMs = Math.abs(Number(ms));

    const { showSeconds = false, format = 'auto' } = options;

    // 计算各时间单位
    const day = Math.floor(absMs / (24 * 60 * 60 * 1000));
    const hour = Math.floor((absMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minute = Math.floor((absMs % (60 * 60 * 1000)) / (60 * 1000));
    const second = Math.floor((absMs % (60 * 1000)) / 1000);

    // 根据格式生成结果
    let result = '';

    if (format === 'full') {
      // 完整格式：始终显示天时分
      result = `${day}天${hour}小时${minute}分钟`;
      if (showSeconds) {
        result += `${second}秒`;
      }
    } else if (format === 'short') {
      // 简短格式：只显示最大的非零单位
      if (day > 0) {
        result = `${day}天`;
      } else if (hour > 0) {
        result = `${hour}小时`;
      } else if (minute > 0) {
        result = `${minute}分钟`;
      } else if (showSeconds || second > 0) {
        result = `${second}秒`;
      } else {
        result = '0分钟';
      }
    } else {
      // 自动格式（默认）：智能省略零值单位
      const parts = [];
      if (day > 0) parts.push(`${day}天`);
      if (hour > 0) parts.push(`${hour}小时`);
      if (minute > 0) parts.push(`${minute}分钟`);

      if (parts.length === 0) {
        // 不足1分钟
        if (showSeconds) {
          parts.push(`${second}秒`);
        } else {
          parts.push(minute > 0 ? `${minute}分钟` : '0分钟');
        }
      } else if (showSeconds && second > 0) {
        parts.push(`${second}秒`);
      }

      result = parts.join('');
    }

    // 添加负号
    return isNegative ? `-${result}` : result;
  },

  /**
   * 获取日期的开始时间
   * @param {*} date 日期
   * @returns
   */
  beginOfDay(date) {
    date = this.convertDate(date);
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  },

  /**
   * 获取日期的结束时间
   * @param {*} date 日期
   * @returns
   */
  endOfDay(date) {
    date = this.convertDate(date);
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  },

  /**
   * 计算两个日期之间的天数
   * @param {*} date1 日期1
   * @param {*} date2 日期2
   * @returns
   */
  betweenDay(date1, date2) {
    date1 = this.convertDate(date1);
    date2 = this.convertDate(date2);
    if (!date1 || !date2) return null;
    // 计算差值
    return Math.floor((date2.getTime() - date1.getTime()) / (24 * 3600 * 1000));
  },

  /**
   * 格式化日期
   * @param {*} date 日期
   * @param {*} fmt 格式
   * @returns
   */
  formatDate(date, fmt = "yyyy-MM-dd HH:mm:ss") {
    date = this.convertDate(date);
    if (!date) return '';
    const o = {
      "M+": date.getMonth() + 1, //月份
      "d+": date.getDate(), //日
      "H+": date.getHours(), //小时
      "m+": date.getMinutes(), //分
      "s+": date.getSeconds(), //秒
      "q+": Math.floor((date.getMonth() + 3) / 3), //季度
      "S": date.getMilliseconds() //毫秒
    };
    if (/(y+)/.test(fmt)) { // 年份
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + "").substr(4 - RegExp.$1.length));
    }
    for (const k in o) {
      if (new RegExp("(" + k + ")").test(fmt)) {
        fmt = fmt.replace(RegExp.$1, (RegExp.$1.length === 1) ? (o[k]) : (("00" + o[k]).substr(("" + o[k]).length)));
      }
    }
    return fmt;
  },

  /**
   * 添加时间
   * @param {*} date 日期
   * @param {*} time 时间
   * @returns
   */
  addTime(date, time) {
    date = this.convertDate(date);
    if (!date) return null;
    return new Date(date.getTime() + time);
  },

  /**
   * 将各种格式的输入转换为 Date 对象
   * @param {Date|string|number} date - 日期，支持 Date 对象、日期字符串、时间戳（毫秒）
   * @returns {Date|null} Date 对象，转换失败返回 null
   * @example
   * DateUtils.convertDate('2024-01-15')           // Date 对象
   * DateUtils.convertDate(1705276800000)          // Date 对象
   * DateUtils.convertDate(new Date())             // Date 对象
   * DateUtils.convertDate(null)                   // null
   * DateUtils.convertDate('invalid')              // null
   */
  convertDate(date) {
    // 处理 null/undefined
    if (date === null || date === undefined) {
      return null;
    }

    // 已经是 Date 对象
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }

    // 数字（时间戳）
    if (typeof date === 'number') {
      const d = new Date(date);
      return isNaN(d.getTime()) ? null : d;
    }

    // 字符串
    if (typeof date === 'string') {
      const trimmed = date.trim();
      if (trimmed === '') {
        return null;
      }

      // 尝试解析字符串
      const d = new Date(trimmed);
      return isNaN(d.getTime()) ? null : d;
    }

    // 其他类型不支持
    return null;
  },



  /**
   * Vue 插件安装方法
   * 挂载 $date 到 Vue 原型，并注册全局过滤器
   * @param {Vue} Vue - Vue 构造函数
   * @example
   * import dateUtils from '@/utils/dateUtils';
   * Vue.use(dateUtils); // 在 main.js 中调用
   */
  install(Vue) {
    // 挂载到 Vue 原型，组件中可通过 this.$date 访问
    Vue.prototype.$date = this;

    // 注册全局过滤器
    // 将毫秒转换为时间字符串，如：xx 分钟
    Vue.filter('dateFormatDuration', (ms, options) => this.formatDuration(ms, options));
    // 获取日期的开始时间
    Vue.filter('dateBeginOfDay', (date) => this.beginOfDay(date));
    // 获取日期的结束时间
    Vue.filter('dateEndOfDay', (date) => this.endOfDay(date));
    // 计算两个日期之间的天数
    Vue.filter('dateBetweenDay', (date1, date2) => this.betweenDay(date1, date2));
    // 格式化日期
    Vue.filter('dateFormat', (date, fmt) => this.formatDate(date, fmt));
    // 添加时间
    Vue.filter('dateAddTime', (date, time) => this.addTime(date, time));
    // 转换日期
    Vue.filter('dateConvert', (date) => this.convertDate(date));
  }
};

export default DateUtils;
